import * as pdfjsLib from 'pdfjs-dist';

// Configure worker for pdfjs-dist v5.x
// Use the bundled worker from node_modules via import
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * Clean extracted text from PDF
 * - Remove multiple spaces
 * - Remove duplicate headers/footers
 * - Normalize broken lines
 */
function cleanPdfText(text: string): string {
  return text
    // Normalize line breaks
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Remove excessive whitespace
    .replace(/[ \t]+/g, ' ')
    // Remove multiple newlines (keep max 2)
    .replace(/\n{3,}/g, '\n\n')
    // Trim lines
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    // Final trim
    .trim();
}

/**
 * Extract text from a PDF file in the browser
 * Uses pdfjs-dist which runs entirely in the browser
 * 
 * @param file - The PDF file to extract text from
 * @returns Promise with extracted text and page count
 */
export async function extractPdfText(file: File): Promise<{ text: string; pageCount: number }> {
  console.log(`[PDF Extractor] Starting extraction for: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
  
  try {
    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    console.log(`[PDF Extractor] File loaded into memory`);
    
    // Load PDF document
    const loadingTask = pdfjsLib.getDocument({ 
      data: arrayBuffer,
      // Disable range requests for simpler loading
      disableRange: true,
      // Use standard fonts
      useSystemFonts: true,
    });
    
    const pdf = await loadingTask.promise;
    const pageCount = pdf.numPages;
    console.log(`[PDF Extractor] PDF loaded: ${pageCount} pages`);
    
    if (pageCount === 0) {
      throw new Error('Il PDF non contiene pagine');
    }
    
    const textParts: string[] = [];
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
      try {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        // Extract text items with proper spacing
        let lastY: number | null = null;
        const pageLines: string[] = [];
        let currentLine = '';
        
        for (const item of textContent.items) {
          const textItem = item as { str?: string; transform?: number[] };
          if (!textItem.str) continue;
          
          // Check if we're on a new line (Y position changed significantly)
          const y = textItem.transform ? textItem.transform[5] : null;
          if (lastY !== null && y !== null && Math.abs(y - lastY) > 5) {
            if (currentLine.trim()) {
              pageLines.push(currentLine.trim());
            }
            currentLine = textItem.str;
          } else {
            // Same line - add space if needed
            if (currentLine && !currentLine.endsWith(' ') && !textItem.str.startsWith(' ')) {
              currentLine += ' ';
            }
            currentLine += textItem.str;
          }
          lastY = y;
        }
        
        // Don't forget the last line
        if (currentLine.trim()) {
          pageLines.push(currentLine.trim());
        }
        
        const pageText = pageLines.join('\n');
        if (pageText.length > 0) {
          textParts.push(pageText);
        }
        
        console.log(`[PDF Extractor] Page ${pageNum}/${pageCount}: ${pageText.length} chars`);
      } catch (pageError) {
        console.warn(`[PDF Extractor] Error on page ${pageNum}:`, pageError);
        // Continue with other pages
      }
    }
    
    // Join all pages and clean
    const rawText = textParts.join('\n\n--- Pagina ---\n\n');
    const cleanedText = cleanPdfText(rawText);
    
    console.log(`[PDF Extractor] Extraction complete: ${cleanedText.length} chars from ${pageCount} pages`);
    
    // Log first 500 chars for debugging
    if (cleanedText.length > 0) {
      console.log(`[PDF Extractor] Preview: ${cleanedText.substring(0, 500)}...`);
    } else {
      console.warn('[PDF Extractor] WARNING: No text extracted! PDF might be scanned/image-based');
    }
    
    return { text: cleanedText, pageCount };
  } catch (error) {
    console.error('[PDF Extractor] Error:', error);
    
    // Provide helpful error messages
    if (error instanceof Error) {
      if (error.message.includes('Invalid PDF')) {
        throw new Error('Il file non è un PDF valido');
      }
      if (error.message.includes('password')) {
        throw new Error('Il PDF è protetto da password');
      }
      throw new Error(`Errore lettura PDF: ${error.message}`);
    }
    
    throw new Error('Errore sconosciuto durante la lettura del PDF');
  }
}
